import { type ClassValue, clsx } from "clsx"
import { NextRequest } from "next/server"

import { twMerge } from "tailwind-merge"

interface PaymentDetails {
    payload: string
}

interface FormDataPayload {
    centraPaymentMethod: string
    details: PaymentDetails
    paymentData: string
}

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

type PaymentInfo = {
    centraPaymentMethod: string
    details: { [key: string]: string }
    paymentData: string
}

export function mergePaymentData(
    centraPaymentMethod: string,
    details: Array<{ name: string; value: string }>
): PaymentInfo {
    const paymentInfo: PaymentInfo = {
        centraPaymentMethod: centraPaymentMethod,
        details: {},
        paymentData: ""
    }

    details.forEach(detail => {
        if (detail.name === "details[payload]") {
            paymentInfo.details["payload"] = detail.value
        } else if (detail.name === "paymentData") {
            paymentInfo.paymentData = detail.value
        }
    })

    return paymentInfo
}

export async function mergeFormData(request: NextRequest): Promise<FormDataPayload> {
    const formData = await request.formData()
    const detailsPayload = formData.get("details[payload]")
    const paymentData = formData.get("paymentData")

    const payload: FormDataPayload = {
        centraPaymentMethod: "adyen-drop-in-eu-test",
        details: {
            payload: detailsPayload ? detailsPayload.toString() : ""
        },
        paymentData: paymentData ? paymentData.toString() : ""
    }

    return payload
}

export function transformAndStringifyPaymentResultData(input: any) {
    const { token, order, location } = input

    const transformedData = {
        token,
        order: {
            ...order,
            items: order.items.map((item: any) => ({
                ...item
            }))
        },
        location: {
            ...location
        }
    }

    return JSON.stringify(transformedData)
}

const envBaseUrls: { [key: string]: string } = {
    localhost: "https://localhost:3000",
    production: "https://qa-pocsports-ecom.vercel.app",
    qa: "https://qa-pocsports-ecom.vercel.app",
    development: "https://localhost:3000",
    preproduction: "https://beta-poc.vercel.app"
}

export const getEnvironmentBaseUrl = (environment: string): string => {
    return envBaseUrls[environment] || envBaseUrls.local
}

export function convertSnakeCaseToUpperCase(input: string): string {
    if (!input) {
        return ""
    }
    return input
        .split("_")
        .map(word => word.toUpperCase())
        .join(" ")
}

export function convertToSnakeCase(input: string): string {
    if (!input) {
        return ""
    }
    return input
        .trim()
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]+/g, "_") // Replace any non-alphanumeric chars with underscore
        .replace(/([A-Z])/g, "_$1") // Add underscore before capital letters
        .replace(/^_+|_+$/g, "") // Remove leading/trailing underscores
        .replace(/_+/g, "_") // Replace multiple underscores with single
}

export const splitOptionValue = (optionValue: string): [string, string] => {
    const splitIndex = optionValue.lastIndexOf("(")
    const deliveryMethod = optionValue.substring(0, splitIndex).trim()
    let deliveryTime = optionValue.substring(splitIndex).trim()
    deliveryTime = deliveryTime.replace(/\(|\)|business days/g, "").trim()
    return [deliveryMethod, deliveryTime]
}

export const removeTrailingSlash = (url: string): string => {
    return url.endsWith("/") ? url.replace(/\/+$/, "") : url
}

/**
 * ------------------------------------------------------------------------------------------------
 * Handle redirects based on the redirects.ts file. Is also used in generateHreflangs
 * Generates a regular expression to match dynamic segments in a URL path.
 * @param source - The source path with dynamic segments (e.g., "/:locale/:path*").
 * @returns A regular expression that matches the dynamic segments in the source path.
 */
export const generateRedirectRegex = (source: string) => {
    return new RegExp(
        "^" +
            source
                // Replace all $ with \$
                .replace(/\$/g, "\\$")
                .replace(/:\w+\*/g, "(.*)") // Handle `:path*`
                .replace(/:\w+/g, "([^/]+)") + // Handle `:locale` or other dynamic segments
            "$"
    )
}

export const breakWordsAtSlash = (string: string) => {
    return string.replace(/\//g, "/\u200B")
}

export const numberOrDefault = (value: any, defaultValue: number = 0): number => {
    const parsedValue = Number(value)
    return isNaN(parsedValue) ? defaultValue : parsedValue
}
